import axios from 'axios';
import apiConfig from '@/config/api.config';
import {
  getToken,
  setToken,
  removeToken
} from './token';
import { setCid, removeCid } from './cid';
import { shouldBlockMembershipGraceRequest } from './membershipRequestPolicy';

const request = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  withCredentials: true
});

const refreshClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  withCredentials: true
});

let isRefreshing = false;
let pendingQueue = [];
let membershipGraceReadOnly = false;

const setMembershipGraceReadOnly = (readOnly) => {
  membershipGraceReadOnly = !!readOnly;
};

const clearAuthState = () => {
  removeToken();
  removeCid();
};

const readCookie = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }
  const prefix = `${name}=`;
  const item = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return item ? decodeURIComponent(item.substring(prefix.length)) : '';
};

const getCsrfToken = () => readCookie('__Host-zjyz_csrf') || readCookie('zjyz_csrf_dev');

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const postRefresh = async (allowStaleRetry = true) => {
  const csrfToken = getCsrfToken();
  const headers = csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
  const refreshResp = await refreshClient.post('/user/refresh', {}, { headers });
  const refreshResult = refreshResp.data;

  if (refreshResult?.returnCode === 'AUTH_REFRESH_STALE' && allowStaleRetry) {
    await wait(150);
    return postRefresh(false);
  }

  const refreshData = refreshResult?.data;
  const nextAccessToken = refreshData?.accessToken;
  if (!refreshResult?.succeed || !nextAccessToken) {
    throw new Error(refreshResult?.errorMessage || '刷新登录态失败');
  }
  setToken(nextAccessToken);
  if (refreshData?.cid) {
    setCid(refreshData.cid);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('auth:cid-changed', { detail: { cid: refreshData.cid } }));
    }
  }
  return refreshResult;
};

const runWithRefreshLock = async () => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('zjyz-auth-refresh', { mode: 'exclusive' }, () => postRefresh());
  }
  return postRefresh();
};

const refreshSession = async () => {
  return runWithRefreshLock();
};

const enqueuePendingRequest = () => new Promise((resolve, reject) => {
  pendingQueue.push({ resolve, reject });
});

const flushPendingQueue = (error, token) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  pendingQueue = [];
};

const redirectToLogin = () => {
  if (window.location.pathname.toLowerCase() !== '/login') {
    window.location.href = '/login';
  }
};

request.interceptors.request.use((config) => {
  if (shouldBlockMembershipGraceRequest(config, membershipGraceReadOnly)) {
    const error = new Error('当前为会员宽限期，仅可查看、导出和打印数据');
    error.returnCode = 'MEMBERSHIP_READ_ONLY';
    return Promise.reject(error);
  }
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

request.interceptors.response.use(async (response) => {
  const data = response.data;
  let authPayload = data;

  // 下载接口通常使用 blob；若后端返回 JSON 错误体，需要先解析后再做鉴权判断
  if (typeof Blob !== 'undefined' && data instanceof Blob && data.type && data.type.includes('application/json')) {
    try {
      const text = await data.text();
      authPayload = JSON.parse(text);
    } catch (e) {
      authPayload = data;
    }
  }

  if (authPayload?.returnCode !== 'AUTH401') {
    if (typeof window !== 'undefined' && [
      'MEMBERSHIP_REQUIRED',
      'MEMBERSHIP_EXPIRED',
      'MEMBERSHIP_SUSPENDED',
      'MEMBERSHIP_READ_ONLY',
      'ACCOUNT_LIMIT_REACHED',
    ].includes(authPayload?.returnCode)) {
      window.dispatchEvent(new CustomEvent('membership:access-denied', {
        detail: { returnCode: authPayload.returnCode },
      }));
    }
    return data;
  }

  const originalRequest = response.config || {};
  const requestUrl = originalRequest.url || '';
  const isAuthApi = requestUrl.includes('/user/login')
    || requestUrl.includes('/user/register')
    || requestUrl.includes('/user/refresh')
    || requestUrl.includes('/user/logout');

  if (originalRequest._retryAuth || isAuthApi) {
    clearAuthState();
    redirectToLogin();
    return Promise.reject(new Error(authPayload?.errorMessage || '登录状态已失效，请重新登录'));
  }

  if (isRefreshing) {
    try {
      const nextToken = await enqueuePendingRequest();
      originalRequest._retryAuth = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${nextToken}`;
      return request(originalRequest);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  isRefreshing = true;
  originalRequest._retryAuth = true;

  try {
    const refreshResult = await refreshSession();
    const nextAccessToken = refreshResult.data.accessToken;

    flushPendingQueue(null, nextAccessToken);
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
    return request(originalRequest);
  } catch (error) {
    flushPendingQueue(error, null);
    clearAuthState();
    redirectToLogin();
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
}, (error) => {
  const status = error?.response?.status;
  if (status === 401) {
    clearAuthState();
    redirectToLogin();
  }
  return Promise.reject(error);
});

export { request, refreshSession, getCsrfToken, setMembershipGraceReadOnly };
